import * as t from "io-ts"
import { Branded } from "io-ts"
import * as F from "fp-ts/function"
import * as E from "fp-ts/Either"
import { AType, EType, summon } from "@silversea-ssc/functional-support"

export const dateOnlyType = new t.Type<Date, string, unknown>(
    "DateOnly",
    (u): u is Date => u instanceof Date,
    (u, c) =>
        F.pipe(
            t.string.validate(u, c),
            E.chain((s) => {
                const match = /^ *(?<year>\d\d\d\d)-(?<month>\d\d)-(?<day>\d\d)(T00:00:00)? *$/.exec(s)
                const { year, month, day } = match?.groups || {}
                return year && month && day
                    ? t.success(new Date(Date.UTC(parseInt(year), parseInt(month) - 1, parseInt(day))))
                    : t.failure(u, c)
            }),
        ),
    (a) => a.toISOString().split("T")[0],
)

const _DateOnly = summon((F) =>
    F.date({
        IoTsURI: () => dateOnlyType,
        FastCheckURI: (_x, { fastCheck }) =>
            fastCheck
                .date({ min: new Date(1970, 0, 1, 0, 0, 0, 0), max: new Date(2100, 12, 31, 23, 59, 59, 999) })
                .map(
                    (date: Date) => new Date(Date.UTC(date.getFullYear(), date.getMonth(), date.getDate(), 0, 0, 0, 0)),
                ),
    }),
)
export type DateOnly = AType<typeof _DateOnly>
export type DateOnlyEncoded = EType<typeof _DateOnly>

const buildDateOnlyFromNumber = (dateAsInt: Branded<number, ValidNumberAsDateBrand>) => {
    const day = Day.fromDateAsNumber(dateAsInt)
    const month = Month.fromDateAsNumber(dateAsInt)
    const year = Year.fromDateAsNumber(dateAsInt)
    return new Date(Date.UTC(year, month - 1, day))
}
const encodeDateOnlyAsNumber = (a: Date) => Year.toNumber(a) + Month.toNumber(a) + Day.toNumber(a)

export const DateOnly = Object.assign(_DateOnly, {
    buildUnsafe: (yyyyMMdd: string) =>
        F.pipe(
            _DateOnly.strictType.decode(yyyyMMdd),
            E.getOrElse((): DateOnly => {
                throw new Error(`Invalid date only ${yyyyMMdd}`)
            }),
        ),
    today: () => DateOnly.buildUnsafe(new Date().toISOString().split("T")[0]),
    fromNumber: buildDateOnlyFromNumber,
})

export interface ValidNumberAsDateBrand {
    readonly ValidNumberAsDate: unique symbol
}

const validateNumberAsDate = (dateAsInt: unknown): E.Either<string, number> => {
    if (typeof dateAsInt !== "number" || !Number.isInteger(dateAsInt)) return E.left("Must be an integer")
    if (dateAsInt < 0) return E.left("Date must be positive")

    const day = Day.fromDateAsNumber(dateAsInt)
    const month = Month.fromDateAsNumber(dateAsInt)
    const year = Year.fromDateAsNumber(dateAsInt)

    if (day < 1 || day > 31) return E.left("Day must be between 1 and 31")
    if (month < 1 || month > 12) return E.left("Month must be between 1 and 12")
    if (year < 1970) return E.left("Year must be after 1970")
    return E.right(dateAsInt)
}

export const validNumberAsDate = new t.Type<Branded<number, ValidNumberAsDateBrand>, number, unknown>(
    "DateOnlyFromNumber",
    (u): u is Branded<number, ValidNumberAsDateBrand> => E.isRight(validateNumberAsDate(u)),
    (dateAsInt, c) => {
        const validation = validateNumberAsDate(dateAsInt)
        return E.isRight(validation)
            ? t.success(validation.right as number as Branded<number, ValidNumberAsDateBrand>)
            : t.failure(dateAsInt, c, validation.left)
    },
    F.identity,
)
export const ValidNumberAsDate = summon((F) =>
    F.refined(
        F.number(),
        (dateAsInt: number): dateAsInt is Branded<number, ValidNumberAsDateBrand> =>
            E.isRight(validateNumberAsDate(dateAsInt)),
        "PositiveInt",
        {
            IoTsURI: (_x, { withValidate }) => withValidate(_x, validNumberAsDate.validate),
            FastCheckURI: (_x, { fastCheck }) =>
                fastCheck
                    .date({ min: new Date(1970, 0, 1, 0, 0, 0, 0), max: new Date(2100, 12, 31, 23, 59, 59, 999) })
                    .map((date: Date) => encodeDateOnlyAsNumber(date) as Branded<number, ValidNumberAsDateBrand>),
        },
    ),
)
export type ValidNumberAsDate = AType<typeof ValidNumberAsDate>

export const DateOnlyFromNumber = new t.Type<Date, number, unknown>(
    "DateOnlyFromNumber",
    (u): u is Date => u instanceof Date,
    (u, c) =>
        F.pipe(
            validNumberAsDate.validate(u, c),
            E.map((dateAsInt) => buildDateOnlyFromNumber(dateAsInt)),
        ),
    (a) => encodeDateOnlyAsNumber(a),
)

type ParseDateAsNumber = {
    fromDateAsNumber: (dateAsNumber: number) => number
    toNumber: (date: Date) => number
}
const Day: ParseDateAsNumber = {
    fromDateAsNumber: (dateAsInt) => dateAsInt % 100,
    toNumber: (date) => date.getUTCDate(),
}
const Month: ParseDateAsNumber = {
    fromDateAsNumber: (dateAsInt) => Math.floor((dateAsInt / 100) % 100),
    toNumber: (date) => (date.getUTCMonth() + 1) * 100,
}
const Year: ParseDateAsNumber = {
    fromDateAsNumber: (dateAsInt) => Math.floor(dateAsInt / 10000),
    toNumber: (date) => date.getUTCFullYear() * 10000,
}
